import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import useSWR from 'swr'
import { useToast } from '../contexts/ToastContext'
import { useSpeedbump, Button, Badge, Modal } from '../components'
import { useUnsavedChanges } from '../hooks'
import { ConflictError } from '../services/api'
import { Spinner } from '../components/Spinner'
import {
  fetchArtworkTemplate,
  fetchArtworkTemplateAssets,
  inspectArtworkTemplateFont,
  saveArtworkTemplateDraft,
  publishArtworkTemplate,
  rollbackArtworkTemplate,
  previewArtworkTemplate,
  fetchArtworkPreviewMatches,
  uploadArtworkTemplateFont,
  uploadArtworkTemplateImage,
  type ArtworkAssetFont,
  type ArtworkAssetImage,
  type ArtworkPreviewResponse,
} from '../services/api'
import {
  ADDABLE_LAYER_TYPES,
  ANCHOR_POINTS,
  BOUND_FIELD_OPTIONS,
  ENTITY_ASSET_OPTIONS,
  LAYER_CHIPS,
  LAYER_TYPE_LABELS,
  MATCH_TYPES,
  SIZING_MODES,
  TARGETING_CATEGORIES,
  TEAM_OPTIONS,
  TEXT_TRANSFORM_OPTIONS,
  anchorTargets,
  appendLayer,
  boxForSizingMode,
  canvasIsSelectable,
  canvasProblem,
  CONDITIONAL_FIELDS,
  conditionalFieldOf,
  matchTypeIsRedundant,
  MAX_CANVAS_DIMENSION,
  boundPath,
  byZ,
  flattenLayers,
  getLayerAt,
  GROUP_CONDITIONS,
  groupConditionWhen,
  HERO_PHASES,
  MATCH_BOUND_OPTIONS,
  matchBoundPath,
  parseMatchBound,
  imageSourceChoices,
  imageSourceKey,
  isGroup,
  isImage,
  isPlaced,
  isShape,
  isText,
  layerLabel,
  layerIdProblem,
  layerStackZ,
  layerSubtitle,
  makeLayer,
  mapLayerAt,
  normalizeAnchor,
  normalizeTemplateBody,
  parseBound,
  parseGroupCondition,
  pathKey,
  patchLayerAt,
  positionPatch,
  sourceFromChoice,
  targetingIsSelectable,
  removeLayerAt,
  renameLayerId,
  sizingFieldsFor,
  sizingModeOf,
  samePath,
  zOf,
  type AnchorPoint,
  type ConditionalField,
  type ConditionalRule,
  type GroupArm,
  type GroupConditionParams,
  type GroupLayer,
  type ImageLayer,
  type Layer,
  type LayerPath,
  type LayerType,
  type MatchType,
  type Team,
  type PlacedLayer,
  type Point,
  type Targeting,
  type ShapeLayer,
  type SizingMode,
  type TemplateBody,
  type TextLayer,
  unknownTopLevelKeys,
} from '../utils/artworkTemplate'
import selectStyles from '../styles/react-select.module.scss'
import styles from './ArtworkTemplateEditor.module.scss'

type Box = [number, number, number, number] // x, y, w, h

const num = (v: string): number => {
  const n = parseFloat(v)
  return Number.isNaN(n) ? 0 : Math.round(n)
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v))

interface DropdownOption {
  value: string
  label: string
}

/**
 * Free-text-plus-suggestions chips for a conditional rule's values. The schema
 * allows any string, and prod carries values the shortlist doesn't (compound
 * categories the transpiler baked in), so these suggest rather than constrain.
 *
 * Takes its suggestions from the chosen field rather than hardcoding categories,
 * so a second field brings its own without touching this.
 */
function ConditionValueChips({
  values,
  field,
  onChange,
}: {
  values: string[]
  field: ConditionalField
  onChange: (values: string[]) => void
}) {
  return (
    <CreatableSelect
      isMulti
      options={field.suggestions.map((c) => ({ value: c, label: c }))}
      value={values.map((c) => ({ value: c, label: c }))}
      onChange={(opts) => onChange(opts.map((o) => o.value))}
      placeholder={`Any ${field.label.toLowerCase()}…`}
      className={selectStyles.reactSelect}
      classNamePrefix="react-select"
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  )
}

/**
 * The CMS's react-select, with the option/value plumbing folded in — every
 * dropdown in this editor reduces to a string in and a string out, and there
 * are sixteen of them.
 *
 * The menu portals to the body deliberately: these render inside the layer
 * panel and the settings modal, both of which scroll and would clip it.
 */
function Dropdown({
  value,
  options,
  onChange,
  isDisabled,
  title,
  placeholder,
}: {
  value: string
  options: DropdownOption[]
  onChange: (value: string) => void
  isDisabled?: boolean
  title?: string
  placeholder?: string
}) {
  return (
    <div className={styles.dropdownWrap} title={title}>
      <Select
        options={options}
        value={options.find((o) => o.value === value) ?? null}
        onChange={(opt) => onChange(opt?.value ?? '')}
        isDisabled={isDisabled}
        placeholder={placeholder ?? 'Select…'}
        className={selectStyles.reactSelect}
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
  )
}

// All three parts, because family+weight isn't unique: the seeded set has Neue
// Plak Black in both Compressed and Extended, and Awesome Serif Bold in three
// styles. Dropping the style shows identical-looking options and writes an
// ambiguous `font.label` into the body.
const fontLabel = (f: ArtworkAssetFont): string =>
  [f.font_family, f.font_weight, f.font_style].filter(Boolean).join(' ')

// The image types a template body may pin, mirroring the router's
// TEMPLATE_IMAGE_TYPE_PREFIXES. Rename in step with the migration. The labels
// say what the art *is*, since "plate" alone means nothing outside this codebase.
const IMAGE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'matchcard_plate', label: 'Plate — the banner behind the names' },
  { value: 'match_background', label: 'Background — fills the whole canvas' },
  { value: 'matchcard_overlay_asset', label: 'Overlay — a badge or logo' },
]

/** Short form of the same labels, for listing an image that's already chosen. */
const IMAGE_TYPE_LABELS: Record<string, string> = {
  matchcard_plate: 'Plate',
  match_background: 'Background',
  matchcard_overlay_asset: 'Overlay',
}

/**
 * The asset library the pickers read and write. Bundled rather than drilled as
 * four separate props because every picker needs the list *and* the upload —
 * a creative who can only select from what already exists can't build a new
 * template.
 */
interface AssetsBundle {
  images: ArtworkAssetImage[]
  fonts: ArtworkAssetFont[]
  uploadImage: (file: File, typeCode: string) => Promise<ArtworkAssetImage>
  uploadFont: (
    file: File,
    face: { font_family: string; font_weight?: string; font_style?: string },
  ) => Promise<ArtworkAssetFont>
}

/**
 * A copy of `layer` with one optional key gone. The result is still a valid
 * Layer — TS just can't follow "delete an optional property" through the union,
 * so the assertion lives here rather than at each call site. Removing (not
 * nulling) is required: the schema is strict, and `anchor_to: undefined`
 * serializes away while `anchor_to: null` would fail publish.
 */
function withoutKey(layer: Layer, key: 'anchor_to' | 'fallback'): Layer {
  const next: Record<string, unknown> = { ...layer }
  delete next[key]
  return next as unknown as Layer
}

// Whether the template being edited is a hero template. The hero-only
// vocabulary (phase group conditions, match-time text bindings) must not be
// offered while editing a matchcard — its schema rejects them at publish — and
// the deeply nested layer cards shouldn't each thread a prop for one flag.
const HeroTemplateCtx = createContext(false)

export default function ArtworkTemplateEditor() {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useSpeedbump()

  const { data: detail, isLoading, error, mutate } = useSWR(
    templateId ? ['artwork-template', templateId] : null,
    () => fetchArtworkTemplate(templateId as string),
    { revalidateOnFocus: false },
  )
  const { data: previewMatches } = useSWR(
    'artwork-preview-matches',
    fetchArtworkPreviewMatches,
    { revalidateOnFocus: false },
  )
  // Bodies pin assets by id, so the pickers can't work without this lookup.
  const { data: assetData, mutate: mutateAssets } = useSWR(
    'artwork-template-assets',
    fetchArtworkTemplateAssets,
    { revalidateOnFocus: false },
  )

  // Uploads append to the cached list rather than refetching, so the picker can
  // select the new asset in the same interaction that created it.
  const assets: AssetsBundle = useMemo(
    () => ({
      images: assetData?.images ?? [],
      fonts: assetData?.fonts ?? [],
      uploadImage: async (file, typeCode) => {
        const image = await uploadArtworkTemplateImage(file, typeCode)
        await mutateAssets(
          (cur) => ({ images: [...(cur?.images ?? []), image], fonts: cur?.fonts ?? [] }),
          { revalidate: false },
        )
        return image
      },
      uploadFont: async (file, face) => {
        const font = await uploadArtworkTemplateFont(file, face)
        await mutateAssets(
          (cur) => ({ images: cur?.images ?? [], fonts: [...(cur?.fonts ?? []), font] }),
          { revalidate: false },
        )
        return font
      },
    }),
    [assetData, mutateAssets],
  )

  // The visual editor serves matchcard AND hero bodies — same layer
  // vocabulary; hero adds phase groups and match-time bindings (offered only
  // when isHero, since the matchcard schema rejects them). vod_overlay /
  // plate / draw keep the raw-JSON fallback.
  const isHero = detail?.template_type === 'hero'
  const isVisual = detail?.template_type === 'matchcard' || isHero

  // ---- Working document + undo/redo history ------------------------------
  const [draft, setDraft] = useState<TemplateBody | null>(null)
  const [past, setPast] = useState<TemplateBody[]>([])
  const [future, setFuture] = useState<TemplateBody[]>([])
  const [name, setName] = useState('')
  const [selectedPath, setSelectedPath] = useState<LayerPath | null>(null)
  const [saved, setSaved] = useState(true)
  const [saving, setSaving] = useState(false)
  const [addMenuFor, setAddMenuFor] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  // The publish gate reports every schema violation with its JSON path; that's
  // multi-line and too long for a toast, so it gets its own panel.
  const [gateError, setGateError] = useState<string | null>(null)
  // A 409 from save/publish: someone else changed the template since we loaded.
  // Deliberately does NOT refetch the way the merchandising pages do on conflict
  // — their edits are already server-side, whereas the whole body here is only in
  // this component, so refetching would throw away exactly the work we just
  // refused to overwrite. The author keeps their edits and chooses.
  const [conflict, setConflict] = useState<string | null>(null)

  const draftRef = useRef<TemplateBody | null>(null)
  draftRef.current = draft

  // Raw-JSON fallback state (vod_overlay / plate / draw template types).
  const [rawText, setRawText] = useState('')
  const [rawBaseline, setRawBaseline] = useState('')
  const [rawError, setRawError] = useState<string | null>(null)

  useEffect(() => {
    if (!detail) return
    const body = detail.draft_template ?? detail.live_template ?? {}
    if (detail.template_type === 'matchcard' || detail.template_type === 'hero') {
      setDraft(normalizeTemplateBody(body, detail.name ?? detail.key))
    } else {
      const text = JSON.stringify(body ?? {}, null, 2)
      setRawText(text)
      setRawBaseline(text)
      setRawError(null)
    }
    setName(detail.name ?? '')
    setPast([])
    setFuture([])
    setSaved(true)
    setSelectedPath(null)
    setGateError(null)
    setConflict(null)
  }, [detail])

  // ---- Preview (server round-trip) ---------------------------------------
  const [previewMatchId, setPreviewMatchId] = useState<string | null>(null)
  // Hero preview: which lifecycle phase to render. '' = the match's actual
  // phase; a specific value lets the author flip through the badge arms.
  const [previewPhase, setPreviewPhase] = useState('')
  const [rendered, setRendered] = useState<ArtworkPreviewResponse | null>(null)
  const [generating, setGenerating] = useState(false)
  const [dirtyPreview, setDirtyPreview] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const previewToken = useRef(0)
  // Keyed by path: a layer id is not unique across a group's arms, so an
  // id-keyed delta would shift the other arm's overlay too.
  const [pendingDelta, setPendingDelta] = useState<{
    key: string
    dx: number
    dy: number
  } | null>(null)
  // True for the duration of a drag gesture. The preview is suppressed while
  // it's set: pendingDelta is measured from the gesture's start, so a render
  // landing mid-drag returns boxes that already include the movement and the
  // offset would then be counted twice — the overlay and its handles jumping to
  // double the distance and detaching from the cursor.
  const [dragging, setDragging] = useState(false)

  // What the template targets, and whether the chosen match matches it.
  const wantsDoubles = useMemo(() => {
    const types = draft?.targeting?.types ?? []
    return types.includes('doubles') || types.includes('mixed_doubles')
  }, [draft])
  const previewMatchTypeMismatch = useMemo(() => {
    const chosen = previewMatches?.find((m) => m.id === previewMatchId)
    return Boolean(chosen) && chosen!.isDoubles !== wantsDoubles
  }, [previewMatches, previewMatchId, wantsDoubles])

  // Default the preview match to one matching what the template targets.
  useEffect(() => {
    if (previewMatchId || !previewMatches?.length) return
    const match =
      previewMatches.find((m) => m.isDoubles === wantsDoubles) ?? previewMatches[0]
    setPreviewMatchId(match.id)
  }, [previewMatches, previewMatchId, wantsDoubles])

  const draftKey = useMemo(() => (draft ? JSON.stringify(draft) : ''), [draft])

  useEffect(() => {
    if (!isVisual || !draft || !previewMatchId || !templateId) return
    setDirtyPreview(true)
    // Wait for the gesture to finish; onUp clears `dragging`, which re-runs this.
    if (dragging) return
    // A canvas the renderer would refuse: don't spend a request per keystroke on
    // a guaranteed 400. The settings panel explains it.
    if (canvasProblem(draft.canvas)) {
      setPreviewError(null)
      return
    }
    const handle = window.setTimeout(async () => {
      const token = ++previewToken.current
      setGenerating(true)
      setPreviewError(null)
      try {
        const res = await previewArtworkTemplate(templateId, {
          template: draft as unknown as Record<string, unknown>,
          match_id: previewMatchId,
          ...(isHero && previewPhase ? { phase: previewPhase } : {}),
        })
        if (token !== previewToken.current) return
        setRendered(res)
        setDirtyPreview(false)
        setPendingDelta(null)
      } catch (e) {
        if (token !== previewToken.current) return
        setPreviewError(e instanceof Error ? e.message : 'Preview failed')
      } finally {
        if (token === previewToken.current) setGenerating(false)
      }
    }, 450)
    return () => window.clearTimeout(handle)
    // draftKey stands in for draft to avoid re-running on identical bodies.
  }, [draftKey, previewMatchId, previewPhase, isVisual, templateId, dragging]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---- History helpers ----------------------------------------------------
  const pushHistory = useCallback(() => {
    if (draftRef.current) setPast((p) => [...p, clone(draftRef.current as TemplateBody)])
    setFuture([])
    setSaved(false)
  }, [])

  const commit = useCallback(
    (next: TemplateBody) => {
      pushHistory()
      setDraft(next)
    },
    [pushHistory],
  )

  const undo = useCallback(() => {
    setPast((p) => {
      if (!p.length) return p
      const prev = p[p.length - 1]
      if (draftRef.current) setFuture((f) => [clone(draftRef.current as TemplateBody), ...f])
      setDraft(prev)
      setSaved(false)
      return p.slice(0, -1)
    })
  }, [])

  const redo = useCallback(() => {
    setFuture((f) => {
      if (!f.length) return f
      const next = f[0]
      if (draftRef.current) setPast((p) => [...p, clone(draftRef.current as TemplateBody)])
      setDraft(next)
      setSaved(false)
      return f.slice(1)
    })
  }, [])

  // ---- Layer / template mutation -----------------------------------------
  const patchLayer = useCallback(
    (path: LayerPath, patch: Record<string, unknown>) => {
      const d = draftRef.current
      if (!d) return
      commit({ ...d, layers: patchLayerAt(d.layers, path, patch) })
    },
    [commit],
  )

  /** Rename a layer, repointing anchor_to / skip_if_fell_back that named it —
   *  a dangling anchor doesn't error, it silently falls back to absolute. */
  const renameLayer = useCallback(
    (path: LayerPath, nextId: string) => {
      const d = draftRef.current
      if (!d) return
      commit({ ...d, layers: renameLayerId(d.layers, path, nextId) })
    },
    [commit],
  )

  /** Replace the layer at `path` outright (used when a patch would leave stale
   *  keys behind — switching an image source kind, say, where the old kind's
   *  fields are not allowed on the new one). */
  const replaceLayer = useCallback(
    (path: LayerPath, next: Layer) => {
      const d = draftRef.current
      if (!d) return
      commit({ ...d, layers: mapLayerAt(d.layers, path, () => next) })
    },
    [commit],
  )

  // Live (no-history) layer update used during drag; one undo step is pushed
  // at drag start so the whole gesture reverts together.
  const setLayerLive = useCallback((path: LayerPath, patch: Record<string, unknown>) => {
    setDraft((d) => (d ? { ...d, layers: patchLayerAt(d.layers, path, patch) } : d))
    setSaved(false)
  }, [])

  const patchTemplate = useCallback(
    (patch: Partial<TemplateBody>) => {
      const d = draftRef.current
      if (!d) return
      commit({ ...d, ...patch })
    },
    [commit],
  )

  const patchTargeting = useCallback(
    (patch: Partial<TemplateBody['targeting']>) => {
      const d = draftRef.current
      if (!d) return
      commit({ ...d, targeting: { ...d.targeting, ...patch } })
    },
    [commit],
  )

  /**
   * Rename the template. The body carries its own `name` — required by the
   * schema, minLength 1 — but there's no reason for an author to maintain two,
   * so the body's follows the identity row's. Falls back to the key when the
   * name is blank, since an empty body name fails publish.
   */
  const renameTemplate = useCallback(
    (value: string) => {
      setName(value)
      setSaved(false)
      const d = draftRef.current
      if (d) commit({ ...d, name: value.trim() || (detail?.key ?? d.name) })
    },
    [commit, detail?.key],
  )

  /** Drop top-level keys the v2 schema rejects (a v1 body's leftovers) by
   *  rebuilding the body from the four keys the schema allows. */
  const dropUnknownKeys = useCallback(() => {
    const d = draftRef.current
    if (!d) return
    commit({ name: d.name, canvas: d.canvas, targeting: d.targeting, layers: d.layers })
  }, [commit])

  const addLayer = useCallback(
    (type: LayerType, parent: { path: LayerPath; arm: GroupArm } | null) => {
      const d = draftRef.current
      if (!d) return
      const layer = makeLayer(type, d.layers, d.canvas)
      const { layers, path } = appendLayer(d.layers, parent, layer)
      commit({ ...d, layers })
      setSelectedPath(path)
      setAddMenuFor(null)
    },
    [commit],
  )

  const deleteLayer = useCallback(
    (path: LayerPath) => {
      const d = draftRef.current
      if (!d) return
      commit({ ...d, layers: removeLayerAt(d.layers, path) })
      setSelectedPath(null)
    },
    [commit],
  )

  /**
   * Reorder by swapping z with the neighbour in z order. z is global once the
   * engine expands groups, so this works across group arms too — a layer inside
   * a `then` arm can sit between two root layers.
   */
  const moveLayer = useCallback(
    (path: LayerPath, dir: -1 | 1) => {
      const d = draftRef.current
      if (!d) return
      const ordered = byZ(flattenLayers(d.layers).filter(({ layer }) => isPlaced(layer)))
      const idx = ordered.findIndex((e) => samePath(e.path, path))
      const swapIdx = idx + dir
      if (idx < 0 || swapIdx < 0 || swapIdx >= ordered.length) return
      const a = ordered[idx]
      const b = ordered[swapIdx]
      let layers = patchLayerAt(d.layers, a.path, { z: zOf(b.layer) })
      layers = patchLayerAt(layers, b.path, { z: zOf(a.layer) })
      commit({ ...d, layers })
    },
    [commit],
  )

  // ---- Preview geometry + drag/resize ------------------------------------
  const wsRef = useRef<HTMLDivElement>(null)
  const [ws, setWs] = useState({ w: 900, h: 460 })
  const scaleRef = useRef(1)
  const dragRef = useRef<{
    mode: 'move' | 'resize'
    path: LayerPath
    /** pathKey(path) — the box map's key, and unique across group arms. */
    key: string
    handle?: string
    startX: number
    startY: number
    startPoint: Point
    startBox: Box
    layer: PlacedLayer
    /** Set once the gesture actually moves; see the comment in startDrag. */
    edited: boolean
  } | null>(null)

  useEffect(() => {
    const el = wsRef.current
    if (!el || !window.ResizeObserver) return
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect()
      setWs((cur) =>
        Math.abs(r.width - cur.w) > 1 || Math.abs(r.height - cur.h) > 1
          ? { w: r.width, h: r.height }
          : cur,
      )
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const canvasDims = rendered
    ? { width: rendered.width, height: rendered.height }
    : draft?.canvas ?? { width: 1920, height: 1080 }
  const scale = Math.max(
    0.02,
    Math.min((ws.w - 70) / canvasDims.width, (ws.h - 60) / canvasDims.height),
  )
  scaleRef.current = scale

  const boxes = useMemo(() => rendered?.layer_boxes ?? {}, [rendered])

  // Boxes come back keyed by each drawn layer's body path, which is the only
  // identity that survives group arms — the seeded doubles cards reuse 13 ids
  // across then/else, so an id would address two different layers.
  const placed = useMemo(
    () =>
      flattenLayers(draft?.layers ?? [])
        .filter((e): e is { layer: PlacedLayer; path: LayerPath } => isPlaced(e.layer))
        .map((e) => e as { layer: PlacedLayer; path: LayerPath }),
    [draft],
  )

  const startDrag = useCallback(
    (e: ReactMouseEvent, path: LayerPath, mode: 'move' | 'resize', handle?: string) => {
      e.stopPropagation()
      const d = draftRef.current
      if (!d) return
      const layer = getLayerAt(d.layers, path)
      if (!layer || !isPlaced(layer)) return
      const box = boxes[pathKey(path)]
      if (!box) return
      if (mode === 'move') setSelectedPath(path)
      // No pushHistory here: mousedown is also how a layer gets selected, and
      // marking the template dirty for a click meant inspecting three layers
      // disabled Publish and left three undo steps that restore an identical
      // body. History is pushed on the first actual movement instead.
      const start = layer.anchor_to
        ? layer.anchor_to.offset ?? [0, 0]
        : layer.type === 'image'
          ? layer.box?.at ?? layer.at ?? [0, 0]
          : layer.at ?? [0, 0]
      dragRef.current = {
        mode,
        path,
        key: pathKey(path),
        handle,
        startX: e.clientX,
        startY: e.clientY,
        startPoint: [start[0], start[1]],
        startBox: box,
        layer,
        edited: false,
      }
    },
    [boxes],
  )

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const sc = scaleRef.current || 1
      const dx = Math.round((e.clientX - drag.startX) / sc)
      const dy = Math.round((e.clientY - drag.startY) / sc)
      if (!dx && !dy) return
      if (!drag.edited) {
        // One undo entry per gesture, capturing the body as it was before the
        // drag — so undo returns to where the layer started, not to a midpoint.
        // Both flags arm here rather than on mousedown, so a click that only
        // selects neither dirties the template nor costs a preview render.
        drag.edited = true
        pushHistory()
        setDragging(true)
      }

      if (drag.mode === 'move') {
        const point: Point = [drag.startPoint[0] + dx, drag.startPoint[1] + dy]
        setLayerLive(drag.path, positionPatch(drag.layer, point))
        setPendingDelta({ key: drag.key, dx, dy })
        return
      }

      const [, , bw, bh] = drag.startBox
      const h = drag.handle ?? 'se'
      let nw = bw
      let nh = bh
      let odx = 0
      let ody = 0
      if (h.includes('e')) nw = Math.max(12, bw + dx)
      if (h.includes('s')) nh = Math.max(12, bh + dy)
      if (h.includes('w')) {
        nw = Math.max(12, bw - dx)
        odx = bw - nw
      }
      if (h.includes('n')) {
        nh = Math.max(12, bh - dy)
        ody = bh - nh
      }

      const patch: Record<string, unknown> = {}
      if (drag.layer.type === 'text') {
        // Text has no height of its own — the font size sets it. Only the wrap
        // width is resizable, which is why text gets e/w handles only.
        patch.max_width = nw
      } else if (drag.layer.type === 'shape') {
        patch.size = [nw, nh]
      } else {
        patch.box = { ...(drag.layer.box ?? {}), size: [nw, nh] }
      }
      if (odx || ody) {
        const point: Point = [drag.startPoint[0] + odx, drag.startPoint[1] + ody]
        Object.assign(patch, positionPatch(drag.layer, point))
        // positionPatch rewrites `box` for images; fold the new size back in.
        if (drag.layer.type === 'image' && patch.box) {
          patch.box = { ...(patch.box as object), size: [nw, nh] }
        }
      }
      setLayerLive(drag.path, patch)
      setPendingDelta({ key: drag.key, dx: odx, dy: ody })
    }
    const onUp = () => {
      const moved = Boolean(dragRef.current?.edited)
      dragRef.current = null
      // Lets the suppressed preview run now the gesture is done. Only if the
      // layer actually moved — a click that just selected changed nothing.
      if (moved) setDragging(false)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [setLayerLive, pushHistory])

  // Arrow-key nudge of the selected layer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!selectedPath) return
      const target = e.target as HTMLElement | null
      if (target && /INPUT|SELECT|TEXTAREA/.test(target.tagName)) return
      const map: Record<string, Point> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      }
      const delta = map[e.key]
      if (!delta) return
      const d = draftRef.current
      const layer = d ? getLayerAt(d.layers, selectedPath) : null
      if (!d || !layer || !isPlaced(layer)) return
      e.preventDefault()
      const step = e.shiftKey ? 10 : 1
      const start = layer.anchor_to
        ? layer.anchor_to.offset ?? [0, 0]
        : layer.type === 'image'
          ? layer.box?.at ?? layer.at ?? [0, 0]
          : layer.at ?? [0, 0]
      const point: Point = [start[0] + delta[0] * step, start[1] + delta[1] * step]
      patchLayer(selectedPath, positionPatch(layer, point))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedPath, patchLayer])

  // ---- Save / publish / rollback -----------------------------------------
  const refresh = () => mutate()

  const handleSave = async () => {
    if (!detail) return
    setGateError(null)
    if (!isVisual) {
      let parsed: Record<string, unknown>
      try {
        parsed = JSON.parse(rawText)
      } catch (e) {
        setRawError(e instanceof Error ? e.message : 'Invalid JSON')
        toast('Fix the JSON before saving', 'error')
        return
      }
      setSaving(true)
      try {
        await saveArtworkTemplateDraft(detail.id, {
          template: parsed,
          name,
          expected_updated_at: detail.updated_at ?? '',
        })
        await refresh()
        setRawBaseline(rawText)
        setSaved(true)
        toast('Draft saved', 'success')
      } catch (err) {
        if (err instanceof ConflictError) {
          setConflict(err.message)
          toast('Not saved — someone else changed this template', 'error')
        } else {
          toast(err instanceof Error ? err.message : 'Failed to save draft', 'error')
        }
      } finally {
        setSaving(false)
      }
      return
    }
    if (!draft) return
    setSaving(true)
    try {
      await saveArtworkTemplateDraft(detail.id, {
        template: draft as unknown as Record<string, unknown>,
        name,
        expected_updated_at: detail.updated_at ?? '',
      })
      await refresh()
      setSaved(true)
      toast('Draft saved', 'success')
    } catch (err) {
      if (err instanceof ConflictError) {
        setConflict(err.message)
        toast('Not saved — someone else changed this template', 'error')
      } else {
        toast(err instanceof Error ? err.message : 'Failed to save draft', 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    if (!detail) return
    if (!saved || rawDirty) {
      toast('Save the draft before publishing', 'error')
      return
    }
    const ok = await confirm({
      title: `Publish “${name || detail.key}”?`,
      message:
        'This makes the current draft the live template. New cards render from it immediately.',
      confirmLabel: 'Publish',
    })
    if (!ok) return
    setSaving(true)
    setGateError(null)
    try {
      await publishArtworkTemplate(detail.id, detail.updated_at ?? '')
      await refresh()
      toast('Published', 'success')
    } catch (err) {
      // A 400 here is the schema gate. Surface the whole report — it names each
      // offending path, which is the only way to find a typo'd key in a body
      // this size.
      if (err instanceof ConflictError) {
        setConflict(err.message)
        toast('Not published — someone else changed this template', 'error')
      } else {
        const message = err instanceof Error ? err.message : 'Failed to publish'
        setGateError(message)
        toast('Publish rejected — see the report below', 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleRollback = async (versionId: string, version: number) => {
    if (!detail) return
    const ok = await confirm({
      title: `Roll back to v${version}?`,
      message: 'This makes that version live again. The current live version is archived.',
      confirmLabel: 'Roll back',
      confirmVariant: 'danger',
    })
    if (!ok) return
    setSaving(true)
    try {
      await rollbackArtworkTemplate(detail.id, versionId)
      await refresh()
      toast(`Rolled back to v${version}`, 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to roll back', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleReloadAfterConflict = async () => {
    const ok = await confirm({
      title: 'Discard your changes?',
      message:
        'The editor will reload with the other editor’s version. Anything you ' +
        'changed since your last successful save is lost.',
      confirmLabel: 'Discard and reload',
      confirmVariant: 'danger',
    })
    if (!ok) return
    setConflict(null)
    await refresh()
  }

  const rawDirty = !isVisual && rawText !== rawBaseline
  const dirty = isVisual ? !saved : rawDirty || !saved
  const hasDraft = Boolean(detail?.draft_version)

  // This editor tracks its own saved state (including the raw-JSON tab), so it
  // just hands the answer to the app-wide guards.
  useUnsavedChanges(dirty)

  // ---- Render -------------------------------------------------------------
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.errorBox}>
          <h2>Couldn’t load this template</h2>
          <p>{(error as Error).message}</p>
          <Button variant="secondary" onClick={() => navigate('/artwork-templates')}>
            Back to templates
          </Button>
        </div>
      </div>
    )
  }

  if (isLoading || !detail) {
    return (
      <div className={styles.page}>
        <Spinner size="large" message="Loading template…" />
      </div>
    )
  }

  const selected =
    isVisual && draft && selectedPath ? getLayerAt(draft.layers, selectedPath) : null
  const selectedPlaced = selected && isPlaced(selected) ? selected : null

  const status = generating
    ? { label: 'Regenerating…', cls: styles.statusAmber }
    : previewError
      ? { label: 'Preview error', cls: styles.statusError }
      : dirtyPreview
        ? { label: 'Preview outdated', cls: styles.statusAmber }
        : rendered
          ? { label: 'Preview up to date', cls: styles.statusGreen }
          : { label: 'No preview yet', cls: styles.statusAmber }

  const zOrdered = byZ(placed)

  return (
    <HeroTemplateCtx.Provider value={isHero}>
    <div className={styles.page}>
      {/* Title bar */}
      <div className={styles.titleBar}>
        <h1 className={styles.title}>{name || detail.key}</h1>
        {isVisual && (
          <button
            className={styles.gearBtn}
            title="Template settings"
            aria-label="Template settings"
            onClick={() => setSettingsOpen(true)}
          >
            ⚙
          </button>
        )}
        <div className={styles.spacer} />
        {isVisual && (
          <div className={styles.undoGroup}>
            <button
              className={styles.undoBtn}
              disabled={!past.length}
              title="Undo"
              onClick={undo}
            >
              ↶
            </button>
            <span className={styles.undoDivider} />
            <button
              className={styles.undoBtn}
              disabled={!future.length}
              title="Redo"
              onClick={redo}
            >
              ↷
            </button>
          </div>
        )}
        {/* The version label is the way into the history — there's nowhere else
            to click, so it has to read as a control. */}
        <button
          className={styles.versionLabel}
          title="Version history"
          onClick={() => setHistoryOpen(true)}
        >
          <span className={styles.versionDot} />
          {detail.live_version ? `Live v${detail.live_version.version}` : 'Unpublished'}
          {dirty ? ' · editing' : hasDraft ? ' · draft saved' : ''}
        </button>
        <Button variant="secondary" onClick={() => void handleSave()} disabled={saving || !dirty}>
          Save draft
        </Button>
        <Button
          variant="primary"
          onClick={() => void handlePublish()}
          disabled={saving || !hasDraft || dirty}
        >
          Publish
        </Button>
      </div>

      {conflict && (
        <div className={styles.conflictError}>
          <strong>Your changes were not saved</strong>
          <p>{conflict}</p>
          <p>
            They’re still here, so copy anything you need before reloading —
            reloading replaces the editor with their version.
          </p>
          <Button
            variant="secondary"
            size="small"
            onClick={() => void handleReloadAfterConflict()}
            disabled={saving}
          >
            Reload and discard my changes
          </Button>
        </div>
      )}

      {gateError && (
        <div className={styles.gateError}>
          <strong>Publish rejected by the schema gate</strong>
          <pre>{gateError}</pre>
        </div>
      )}

      {!isVisual ? (
        <RawJsonEditor
          templateType={detail.template_type}
          value={rawText}
          error={rawError}
          onChange={(v) => {
            setRawText(v)
            setRawError(null)
          }}
        />
      ) : (
        <>
          {/* Editor row: layers | preview */}
          <div className={styles.editorRow}>
            {/* Layers panel */}
            <div className={styles.layersPanel}>
              <div className={styles.layersHeader}>
                <span className={styles.panelLabel}>Layers</span>
                <AddLayerButton
                  menuKey="root"
                  openKey={addMenuFor}
                  onToggle={setAddMenuFor}
                  onAdd={(t) => addLayer(t, null)}
                />
              </div>
              <div className={styles.layerList}>
                <LayerTree
                  layers={draft?.layers ?? []}
                  prefix={[]}
                  selectedPath={selectedPath}
                  zOrdered={zOrdered}
                  addMenuFor={addMenuFor}
                  onSelect={setSelectedPath}
                  onMove={moveLayer}
                  onAdd={addLayer}
                  onToggleAddMenu={setAddMenuFor}
                />
                {!draft?.layers.length && (
                  <p className={styles.emptyHint}>No layers yet. Use + to add one.</p>
                )}
              </div>
            </div>

            {/* Preview panel */}
            <div className={styles.previewPanel}>
              <div className={styles.previewToolbar}>
                <span className={`${styles.statusPill} ${status.cls}`}>
                  <span className={styles.statusDot} />
                  {status.label}
                </span>
                {generating && <div className={styles.spinner} />}
                <div className={styles.spacer} />
                <label className={styles.toolbarField}>
                  Preview match
                  <Dropdown
                    value={previewMatchId ?? ''}
                    options={(previewMatches ?? []).map((m) => ({
                      value: m.id,
                      // Singles/doubles is labelled because it decides which
                      // group arms and players[1] layers render at all.
                      label: [
                        m.label,
                        m.isDoubles ? 'doubles' : 'singles',
                        m.category,
                      ]
                        .filter(Boolean)
                        .join(' · '),
                    }))}
                    onChange={(v) => setPreviewMatchId(v || null)}
                    placeholder={previewMatches?.length ? 'Select…' : 'No matches'}
                  />
                </label>
                {isHero && (
                  <label className={styles.toolbarField}>
                    Phase
                    <Dropdown
                      value={previewPhase}
                      options={[
                        { value: '', label: 'Actual phase' },
                        ...HERO_PHASES.map((p) => ({ value: p, label: p })),
                      ]}
                      onChange={(v) => setPreviewPhase(v)}
                    />
                  </label>
                )}
              </div>
              {previewError && <p className={styles.jsonError}>{previewError}</p>}
              {/* A doubles template previewed against a singles match renders
                  none of its per-player layers, so they get no box and can't be
                  positioned — worth saying rather than looking broken. */}
              {!previewError && previewMatchTypeMismatch && (
                <div className={styles.assetWarning}>
                  <strong>
                    This template targets {wantsDoubles ? 'doubles' : 'singles'}, but
                    the preview match is {wantsDoubles ? 'singles' : 'doubles'}
                  </strong>
                  <p>
                    Layers bound to players this match doesn’t have render as
                    nothing and get no draggable box. Pick a{' '}
                    {wantsDoubles ? 'doubles' : 'singles'} match above.
                  </p>
                </div>
              )}
              {/* A render can succeed and still be blank: every asset-load
                  failure comes back as null from the loader, so a black canvas
                  is indistinguishable from a correct render of nothing. */}
              {!previewError && !!rendered?.failed_assets?.length && (
                <div className={styles.assetWarning}>
                  <strong>
                    {rendered.failed_assets.length} asset
                    {rendered.failed_assets.length === 1 ? '' : 's'} couldn’t be
                    loaded
                  </strong>
                  <p>
                    Those layers rendered as nothing. Usually the API can’t reach
                    the images bucket (credentials or <code>IMAGES_BUCKET</code>),
                    rather than anything wrong with the template.
                  </p>
                  <ul>
                    {rendered.failed_assets.slice(0, 6).map((k) => (
                      <li key={k}>{k}</li>
                    ))}
                    {rendered.failed_assets.length > 6 && (
                      <li>…and {rendered.failed_assets.length - 6} more</li>
                    )}
                  </ul>
                </div>
              )}
              <div
                ref={wsRef}
                className={styles.workspace}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedPath(null)
                    setAddMenuFor(null)
                  }
                }}
              >
                <div className={styles.canvasOuter} style={{ transform: `scale(${scale})` }}>
                  <div
                    className={styles.canvas}
                    style={{ width: canvasDims.width, height: canvasDims.height }}
                  >
                    {rendered ? (
                      <img className={styles.renderImg} src={rendered.image} alt="Template preview" />
                    ) : (
                      <div className={styles.canvasEmpty}>
                        {previewMatchId ? 'Rendering…' : 'Pick a preview match'}
                      </div>
                    )}
                    {generating && <div className={styles.veil} />}

                    {/* Draggable overlays from the engine's layer boxes. A layer
                        with no box didn't draw (missing asset, unrendered group
                        arm) and gets no overlay. */}
                    {zOrdered.map(({ path }) => {
                      const box = boxes[pathKey(path)]
                      if (!box) return null
                      const isSel = samePath(path, selectedPath)
                      const shift =
                        pendingDelta && pendingDelta.key === pathKey(path)
                          ? pendingDelta
                          : { dx: 0, dy: 0 }
                      return (
                        <div
                          key={pathKey(path)}
                          className={`${styles.overlay} ${isSel ? styles.overlaySel : ''}`}
                          style={{
                            left: box[0] + shift.dx,
                            top: box[1] + shift.dy,
                            width: box[2],
                            height: box[3],
                            borderWidth: (isSel ? 1.6 : 1.2) / scale,
                          }}
                          onMouseDown={(e) => startDrag(e, path, 'move')}
                        />
                      )
                    })}

                    {/* Resize handles for the selected layer */}
                    {selectedPlaced &&
                      selectedPath &&
                      boxes[pathKey(selectedPath)] &&
                      (() => {
                        const [bx, by, bw, bh] = boxes[pathKey(selectedPath)]
                        const shift =
                          pendingDelta && pendingDelta.key === pathKey(selectedPath)
                            ? pendingDelta
                            : { dx: 0, dy: 0 }
                        const x = bx + shift.dx
                        const y = by + shift.dy
                        const hs = 11 / scale
                        const off = hs / 2
                        // Text sizes from the font; only its wrap width resizes.
                        const defs: [string, number, number][] =
                          selectedPlaced.type === 'text'
                            ? [
                                ['e', bw, bh / 2],
                                ['w', 0, bh / 2],
                              ]
                            : [
                                ['nw', 0, 0],
                                ['n', bw / 2, 0],
                                ['ne', bw, 0],
                                ['e', bw, bh / 2],
                                ['se', bw, bh],
                                ['s', bw / 2, bh],
                                ['sw', 0, bh],
                                ['w', 0, bh / 2],
                              ]
                        return defs.map(([h, hx, hy]) => (
                          <div
                            key={h}
                            className={styles.handle}
                            style={{
                              left: x + hx - off,
                              top: y + hy - off,
                              width: hs,
                              height: hs,
                              borderWidth: 1.2 / scale,
                            }}
                            onMouseDown={(e) => startDrag(e, selectedPath, 'resize', h)}
                          />
                        ))
                      })()}
                  </div>
                </div>
                <div className={styles.dimsCaption}>
                  {canvasDims.width} × {canvasDims.height} px · drag boxes, arrow keys to nudge
                </div>
              </div>
            </div>
          </div>

          {/* Edit form — layer editing only; template settings live in the modal */}
          <div className={styles.formArea}>
            {selected && selectedPath ? (
              isGroup(selected) ? (
                <GroupForm
                  key={pathKey(selectedPath)}
                  layer={selected}
                  onPatch={(patch) => patchLayer(selectedPath, patch)}
                  onDelete={() => deleteLayer(selectedPath)}
                />
              ) : (
                <LayerForm
                  key={pathKey(selectedPath)}
                  layer={selected}
                  layers={draft?.layers ?? []}
                  path={selectedPath}
                  canvas={draft?.canvas ?? { width: 1920, height: 1080 }}
                  targeting={draft?.targeting}
                  assets={assets}
                  onPatch={(patch) => patchLayer(selectedPath, patch)}
                  onRename={(nextId) => renameLayer(selectedPath, nextId)}
                  onReplace={(next) => replaceLayer(selectedPath, next)}
                  onDelete={() => deleteLayer(selectedPath)}
                />
              )
            ) : (
              <div className={styles.card}>
                <p className={styles.hint}>
                  Select a layer — in the list, or by clicking a box on the preview
                  — to edit it. Drag the boxes to reposition, or nudge with arrow
                  keys. Every change re-runs the <strong>production engine</strong>{' '}
                  against your chosen preview match. Canvas and targeting live in{' '}
                  <strong>template settings</strong> (the gear beside the name).
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Template settings — a modal so the panel below the preview stays
          dedicated to whichever layer is selected. */}
      <Modal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Template settings"
        size="large"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSettingsOpen(false)}>
              Close
            </Button>
            <Button
              variant="primary"
              disabled={saving || !dirty}
              onClick={async () => {
                await handleSave()
                setSettingsOpen(false)
              }}
            >
              Save draft
            </Button>
          </>
        }
      >
        <TemplateSettings
          draft={draft}
          templateKey={detail.key}
          name={name}
          onRename={renameTemplate}
          onPatchTemplate={patchTemplate}
          onPatchTargeting={patchTargeting}
          onDropUnknownKeys={dropUnknownKeys}
        />
      </Modal>

      <Modal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Version history"
        size="medium"
      >
        <ul className={styles.historyList}>
          {detail.versions.map((v) => {
            const isLive = detail.live_version?.id === v.id
            return (
              <li key={v.id} className={styles.historyItem}>
                <span className={styles.historyVersion}>v{v.version}</span>
                <Badge
                  variant={isLive ? 'live' : v.status === 'draft' ? 'warning' : 'neutral'}
                  size="small"
                >
                  {isLive ? 'Live' : v.status}
                </Badge>
                <span className={styles.historyMeta}>
                  {v.created_by || 'unknown'}
                  {v.created_at && ` · ${new Date(v.created_at).toLocaleString()}`}
                </span>
                {!isLive && v.status !== 'draft' && (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => void handleRollback(v.id, v.version)}
                    disabled={saving}
                  >
                    Roll back
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      </Modal>

    </div>
    </HeroTemplateCtx.Provider>
  )
}

// ===========================================================================
// Layer tree (groups nest, so the list is recursive)
// ===========================================================================
function AddLayerButton({
  menuKey,
  openKey,
  onToggle,
  onAdd,
  label = '+',
}: {
  menuKey: string
  openKey: string | null
  onToggle: (key: string | null) => void
  onAdd: (type: LayerType) => void
  label?: string
}) {
  const open = openKey === menuKey
  return (
    <div className={styles.addWrap}>
      <button
        className={styles.iconBtn}
        onClick={(e) => {
          e.stopPropagation()
          onToggle(open ? null : menuKey)
        }}
      >
        {label}
      </button>
      {open && (
        <div className={styles.addMenu}>
          {ADDABLE_LAYER_TYPES.map((t) => (
            <button
              key={t}
              className={styles.addMenuRow}
              onClick={(e) => {
                e.stopPropagation()
                onAdd(t)
              }}
            >
              <span className={styles.chip} style={{ background: LAYER_CHIPS[t].color }}>
                {LAYER_CHIPS[t].letter}
              </span>
              {LAYER_CHIPS[t].label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function LayerTree({
  layers,
  prefix,
  selectedPath,
  zOrdered,
  addMenuFor,
  onSelect,
  onMove,
  onAdd,
  onToggleAddMenu,
}: {
  layers: Layer[]
  prefix: LayerPath
  selectedPath: LayerPath | null
  zOrdered: { layer: PlacedLayer; path: LayerPath }[]
  addMenuFor: string | null
  onSelect: (path: LayerPath) => void
  onMove: (path: LayerPath, dir: -1 | 1) => void
  onAdd: (type: LayerType, parent: { path: LayerPath; arm: GroupArm } | null) => void
  onToggleAddMenu: (key: string | null) => void
}) {
  // Front-most first, the same reading order as the canvas stack. Groups sort by
  // where their contents start (layerStackZ), not as z=0 — otherwise every group
  // ties with the background and the list order stops meaning anything.
  const entries = layers.map((layer, i) => ({ layer, path: [...prefix, i] as LayerPath }))
  const sorted = [...entries].sort((a, b) => layerStackZ(b.layer) - layerStackZ(a.layer))

  const zIndexOf = (path: LayerPath) => zOrdered.findIndex((e) => samePath(e.path, path))

  return (
    <>
      {sorted.map(({ layer, path }) => {
        const chip = LAYER_CHIPS[layer.type]
        const key = pathKey(path)
        const zi = zIndexOf(path)
        return (
          <div key={key}>
            <div
              className={`${styles.layerRow} ${samePath(path, selectedPath) ? styles.layerRowSel : ''}`}
              style={{ paddingLeft: 8 + prefix.length * 8 }}
              onMouseDown={() => onSelect(path)}
            >
              <span className={styles.chip} style={{ background: chip.color }}>
                {chip.letter}
              </span>
              <span className={styles.layerName}>
                {layerLabel(layer)}
                <em className={styles.layerSub}>{layerSubtitle(layer)}</em>
              </span>
              {isPlaced(layer) && (
                <span className={styles.layerReorder}>
                  <button
                    title="Bring forward"
                    disabled={zi < 0 || zi >= zOrdered.length - 1}
                    onClick={(e) => {
                      e.stopPropagation()
                      onMove(path, 1)
                    }}
                  >
                    ↑
                  </button>
                  <button
                    title="Send backward"
                    disabled={zi <= 0}
                    onClick={(e) => {
                      e.stopPropagation()
                      onMove(path, -1)
                    }}
                  >
                    ↓
                  </button>
                </span>
              )}
            </div>

            {isGroup(layer) &&
              (['then', 'else'] as GroupArm[]).map((arm) => (
                <div key={arm} className={styles.armBlock}>
                  <div
                    className={styles.armHeader}
                    style={{ paddingLeft: 16 + prefix.length * 8 }}
                  >
                    <span className={styles.armLabel}>
                      {arm === 'then' ? 'when true' : 'otherwise'}
                    </span>
                    <AddLayerButton
                      menuKey={`${key}.${arm}`}
                      openKey={addMenuFor}
                      onToggle={onToggleAddMenu}
                      onAdd={(t) => onAdd(t, { path, arm })}
                    />
                  </div>
                  <LayerTree
                    layers={layer[arm] ?? []}
                    prefix={[...path, arm]}
                    selectedPath={selectedPath}
                    zOrdered={zOrdered}
                    addMenuFor={addMenuFor}
                    onSelect={onSelect}
                    onMove={onMove}
                    onAdd={onAdd}
                    onToggleAddMenu={onToggleAddMenu}
                  />
                </div>
              ))}
          </div>
        )
      })}
    </>
  )
}

// ===========================================================================
// Group edit form
// ===========================================================================
function GroupForm({
  layer,
  onPatch,
  onDelete,
}: {
  layer: GroupLayer
  onPatch: (patch: Record<string, unknown>) => void
  onDelete: () => void
}) {
  const chip = LAYER_CHIPS.group
  const isHero = useContext(HeroTemplateCtx)
  const parsed = parseGroupCondition(layer.when)
  // Hero-only conditions are withheld from matchcard editing (the schema gate
  // would reject the body at publish), but a parsed hero condition in a body
  // is always shown as itself rather than misidentified.
  const kinds = GROUP_CONDITIONS.filter((c) => isHero || !c.heroOnly)
  // An unrecognised condition resolves to nothing at render time, so the group
  // would silently always take the `else` arm. Say so rather than defaulting
  // it away.
  const kind = parsed?.kind ?? kinds[0]
  const params = parsed?.params ?? { team: 'home' as const }

  const setCondition = (next: Partial<GroupConditionParams>, nextKind = kind) =>
    onPatch({ when: groupConditionWhen(nextKind, { ...params, ...next }) })

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.chip} style={{ background: chip.color }}>
          {chip.letter}
        </span>
        <span className={styles.typeLabel}>{LAYER_TYPE_LABELS.group}</span>
        <div className={styles.spacer} />
        <button className={styles.deleteBtn} onClick={onDelete}>
          Delete group
        </button>
      </div>
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <div className={styles.secHead}>Condition</div>

          {!parsed && layer.when?.path !== undefined && (
            <p className={styles.gateInline}>
              <code>{layer.when.path}</code> isn’t a condition this editor
              knows. It resolves to nothing at render time, so the group always
              takes the <strong>otherwise</strong> arm. Choosing below replaces
              it.
            </p>
          )}

          <label className={styles.fieldLabel}>Branch on</label>
          <Dropdown
            value={kind.value}
            options={kinds.map((c) => ({ value: c.value, label: c.label }))}
            onChange={(v) => {
              const next = kinds.find((c) => c.value === v)
              if (next) setCondition({}, next)
            }}
          />

          {/* Each condition declares the parameters it needs; today that's the
              team, but the shape is here so a second kind is a data change. */}
          <div className={styles.row}>
            {kind.params.includes('team') && (
              <div className={styles.inlineField}>
                <label className={styles.fieldLabel}>Team</label>
                <Dropdown
                  value={params.team}
                  options={TEAM_OPTIONS}
                  onChange={(v) => setCondition({ team: v as Team })}
                />
              </div>
            )}
            {kind.params.includes('playerIndex') && (
              <div className={styles.inlineField}>
                <label className={styles.fieldLabel}>Player index</label>
                <input
                  className={styles.input}
                  type="number"
                  value={params.playerIndex ?? 0}
                  onChange={(e) => setCondition({ playerIndex: num(e.target.value) })}
                />
              </div>
            )}
            {kind.params.includes('phase') && (
              <div className={styles.inlineField}>
                <label className={styles.fieldLabel}>Phase</label>
                <Dropdown
                  value={params.phase ?? 'upcoming'}
                  options={HERO_PHASES.map((p) => ({ value: p, label: p }))}
                  onChange={(v) =>
                    setCondition({ phase: v as (typeof HERO_PHASES)[number] })
                  }
                />
              </div>
            )}
          </div>

          <p className={styles.hint}>
            Renders the <strong>when true</strong> arm if the condition holds and
            the <strong>otherwise</strong> arm if not. This is what replaced the
            old per-variant doubles templates — one template branches instead of
            four competing to be selected.
          </p>
          <p className={styles.hint}>
            Stored as <code>when</code>:{' '}
            <code>{JSON.stringify(kind.build(params))}</code>
          </p>
        </div>

        <div className={styles.field}>
          <div className={styles.secHead}>About groups</div>
          <p className={styles.hint}>
            One condition is available today because the schema permits one:{' '}
            <code>when.path</code> must match{' '}
            <code>(home|away).all_have_headshots</code>. The engine can already
            evaluate more — the match category, a single player’s fields, and{' '}
            <code>equals</code>/<code>in</code> comparisons — so widening the
            schema would fill this dropdown without further UI work.
          </p>
          <p className={styles.hint}>
            Groups carry no id and no z of their own: the layers inside them hold
            the z and are ordered against every other layer in the template.
          </p>
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// Layer edit form (image / text / shape)
// ===========================================================================
function LayerForm({
  layer,
  layers,
  path,
  canvas,
  targeting,
  assets,
  onPatch,
  onRename,
  onReplace,
  onDelete,
}: {
  layer: PlacedLayer
  layers: Layer[]
  path: LayerPath
  canvas: { width: number; height: number }
  /** Drives which player slots the image source offers. */
  targeting: Targeting | undefined
  assets: AssetsBundle
  onPatch: (patch: Record<string, unknown>) => void
  onRename: (nextId: string) => void
  onReplace: (next: Layer) => void
  onDelete: () => void
}) {
  const chip = LAYER_CHIPS[layer.type]
  const idProblem = layerIdProblem(layer.id, layers, path)
  const anchored = Boolean(layer.anchor_to)
  const targets = anchorTargets(layers, path)
  // The engine resolves anchor_to against layers it has already drawn, so a
  // reference to a later-drawn (or sibling-arm) layer resolves to nothing and
  // anchor_to is ignored entirely — the layer then can't be moved at all.
  const anchorRefBroken = Boolean(
    layer.anchor_to && !targets.includes(layer.anchor_to.layer)
  )

  const absolute: Point =
    layer.type === 'image' ? layer.box?.at ?? layer.at ?? [0, 0] : layer.at ?? [0, 0]
  const offset: Point = layer.anchor_to?.offset ?? [0, 0]

  const setAbsolute = (point: Point) => {
    if (layer.type === 'image') onPatch({ box: { ...(layer.box ?? {}), at: point } })
    else onPatch({ at: point })
  }

  const setAnchored = (on: boolean) => {
    if (on) {
      onPatch({
        anchor_to: { layer: 'canvas', anchor: 'top-left', self: 'top-left', offset: absolute },
      })
    } else {
      // Drop anchor_to and land at the offset so the layer doesn't jump to 0,0.
      const bare = withoutKey(layer, 'anchor_to')
      onReplace(
        layer.type === 'image'
          ? { ...(bare as ImageLayer), box: { ...(layer.box ?? {}), at: offset } }
          : { ...(bare as TextLayer | ShapeLayer), at: offset },
      )
    }
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.chip} style={{ background: chip.color }}>
          {chip.letter}
        </span>
        <input
          className={`${styles.layerNameInput} ${idProblem ? styles.inputBad : ''}`}
          value={layer.id}
          onChange={(e) => onRename(e.target.value)}
        />
        <span className={styles.typeLabel}>{LAYER_TYPE_LABELS[layer.type]}</span>
        <div className={styles.spacer} />
        <button className={styles.deleteBtn} onClick={onDelete}>
          Delete layer
        </button>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.field}>
          <div className={styles.secHead}>Identity</div>
          <label className={styles.fieldLabel}>Layer ID</label>
          <input
            className={`${styles.input} ${idProblem ? styles.inputBad : ''}`}
            value={layer.id}
            onChange={(e) => onRename(e.target.value)}
          />
          {idProblem ? (
            <p className={styles.gateInline}>{idProblem}</p>
          ) : (
            <p className={styles.hint}>
              Other layers anchor to this ID. Renaming repoints them, so anchors
              don’t break.
            </p>
          )}
          <div className={styles.row}>
            <div className={styles.inlineField}>
              <label className={styles.fieldLabel}>Z (stacking)</label>
              <input
                className={styles.input}
                type="number"
                value={layer.z ?? 0}
                onChange={(e) => onPatch({ z: num(e.target.value) })}
              />
            </div>
          </div>
        </div>

        {isImage(layer) && (
          <ImageSourceField
            layer={layer}
            targeting={targeting}
            assets={assets}
            onPatch={onPatch}
            onReplace={onReplace}
          />
        )}
        {isImage(layer) && (
          <ImageBoxField
            layer={layer}
            layers={layers}
            path={path}
            canvas={canvas}
            onPatch={onPatch}
          />
        )}
        {isText(layer) && <TextContentField layer={layer} onPatch={onPatch} />}
        {isText(layer) && <TextStyleField layer={layer} assets={assets} onPatch={onPatch} />}
        {isShape(layer) && <ShapeStyleField layer={layer} onPatch={onPatch} />}

        {/* Layout & anchoring */}
        <div className={styles.field}>
          <div className={styles.secHead}>Layout &amp; Anchoring</div>
          <label className={styles.fieldLabel}>Attach to</label>
          <Dropdown
            value={anchored ? layer.anchor_to?.layer ?? 'canvas' : ''}
            options={[
              { value: '', label: 'Absolute position' },
              ...targets.map((id) => ({ value: id, label: id === 'canvas' ? 'Canvas' : id })),
              // A stored anchor that no longer qualifies (hand-edited body, or a
              // z change that moved the reference after this layer) stays
              // selectable so it's visible rather than silently swapped out.
              ...(anchorRefBroken
                ? [{ value: layer.anchor_to!.layer, label: `${layer.anchor_to!.layer} (unreachable)` }]
                : []),
            ]}
            onChange={(v) => {
              if (!v) setAnchored(false)
              else if (!anchored) setAnchored(true)
              else onPatch({ anchor_to: { ...layer.anchor_to!, layer: v } })
            }}
          />

          {anchorRefBroken && (
            <p className={styles.gateInline}>
              <code>{layer.anchor_to!.layer}</code> isn’t drawn before this layer,
              so the engine ignores the attachment and falls back to an absolute
              position — dragging this layer will appear to do nothing. Give that
              layer a lower Z, or attach to something else.
            </p>
          )}

          {anchored ? (
            <>
              <div className={styles.anchorRow}>
                <div>
                  <label className={styles.fieldLabel}>On target</label>
                  <AnchorGrid
                    value={normalizeAnchor(layer.anchor_to?.anchor)}
                    onChange={(v) => onPatch({ anchor_to: { ...layer.anchor_to!, anchor: v } })}
                  />
                </div>
                <div>
                  <label className={styles.fieldLabel}>On self</label>
                  <AnchorGrid
                    value={normalizeAnchor(layer.anchor_to?.self ?? layer.anchor_to?.anchor)}
                    onChange={(v) => onPatch({ anchor_to: { ...layer.anchor_to!, self: v } })}
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.inlineField}>
                  <label className={styles.fieldLabel}>Offset X</label>
                  <input
                    className={styles.input}
                    type="number"
                    value={offset[0]}
                    onChange={(e) =>
                      onPatch({
                        anchor_to: {
                          ...layer.anchor_to!,
                          offset: [num(e.target.value), offset[1]],
                        },
                      })
                    }
                  />
                </div>
                <div className={styles.inlineField}>
                  <label className={styles.fieldLabel}>Offset Y</label>
                  <input
                    className={styles.input}
                    type="number"
                    value={offset[1]}
                    onChange={(e) =>
                      onPatch({
                        anchor_to: {
                          ...layer.anchor_to!,
                          offset: [offset[0], num(e.target.value)],
                        },
                      })
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            <div className={styles.row}>
              <div className={styles.inlineField}>
                <label className={styles.fieldLabel}>Position X</label>
                <input
                  className={styles.input}
                  type="number"
                  value={absolute[0]}
                  onChange={(e) => setAbsolute([num(e.target.value), absolute[1]])}
                />
              </div>
              <div className={styles.inlineField}>
                <label className={styles.fieldLabel}>Position Y</label>
                <input
                  className={styles.input}
                  type="number"
                  value={absolute[1]}
                  onChange={(e) => setAbsolute([absolute[0], num(e.target.value)])}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Image source
// ---------------------------------------------------------------------------
/**
 * Choose an image, or bring a new one. A modal rather than an inline select:
 * the list is long, the s3_keys are the only labels there are, and — the reason
 * it changed — the "what kind of asset is this" control only ever described a
 * *pending upload*. Inline beside the picker it read as a filter on the list.
 * Under an Upload heading, what it does is obvious.
 */
function ImagePicker({
  value,
  assets,
  defaultTypeCode = 'matchcard_plate',
  compact = false,
  onChange,
}: {
  value: string | null
  assets: AssetsBundle
  /** Which type an upload started from this picker is recorded as. */
  defaultTypeCode?: string
  /** Thumbnail-only, for the category-rules table where a filename column and a
   *  "Change" label made every row far wider than it needed to be. */
  compact?: boolean
  onChange: (ref: { tc_image_id: string | null; label?: string }) => void
}) {
  const [open, setOpen] = useState(false)
  const [thumbBroken, setThumbBroken] = useState(false)
  const selected = assets.images.find((i) => i.tc_image_id === value) ?? null

  const label = selected
    ? selected.s3_key.split('/').pop()
    : value
      ? `Unlisted image (${value.slice(0, 8)}…)`
      : 'No image'
  const thumb = selected?.cdn_url ?? null

  return (
    <>
      {compact ? (
        // The filename lives in the tooltip and the affordance appears on hover,
        // so a row is only as wide as the picture it shows.
        <button
          className={styles.assetThumbBtn}
          title={`${label} — click to change`}
          aria-label={`Image: ${label}. Click to change.`}
          onClick={() => setOpen(true)}
        >
          {thumb && !thumbBroken ? (
            <img src={thumb} alt="" loading="lazy" onError={() => setThumbBroken(true)} />
          ) : (
            <span className={styles.assetThumbEmpty}>{value ? '?' : 'none'}</span>
          )}
          <span className={styles.assetThumbHover} aria-hidden>
            Change
          </span>
        </button>
      ) : (
        <button
          className={`${styles.assetButton} ${value ? '' : styles.assetButtonEmpty}`}
          title={selected?.s3_key ?? undefined}
          onClick={() => setOpen(true)}
        >
          <span className={styles.assetButtonLabel}>{label}</span>
          <span className={styles.assetButtonHint}>Change</span>
        </button>
      )}
      <ImageLibraryModal
        isOpen={open}
        onClose={() => setOpen(false)}
        value={value}
        assets={assets}
        defaultTypeCode={defaultTypeCode}
        onPick={(ref) => {
          onChange(ref)
          setOpen(false)
        }}
      />
    </>
  )
}

function ImageLibraryModal({
  isOpen,
  onClose,
  value,
  assets,
  defaultTypeCode,
  onPick,
}: {
  isOpen: boolean
  onClose: () => void
  value: string | null
  assets: AssetsBundle
  defaultTypeCode: string
  onPick: (ref: { tc_image_id: string | null; label?: string }) => void
}) {
  const [filter, setFilter] = useState('')
  const [typeFilters, setTypeFilters] = useState<string[]>([])
  // Seeded from the prop, but useState ignores later prop changes and this modal
  // stays mounted with its LayerForm across sizing changes. Without the resync,
  // switching a layer to "Whole canvas" and then uploading files the background
  // as a plate — permanently, since nothing can retype an image afterwards.
  const [uploadType, setUploadType] = useState(defaultTypeCode)
  useEffect(() => {
    if (isOpen) setUploadType(defaultTypeCode)
  }, [isOpen, defaultTypeCode])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  // Thumbnail URLs are derived from CDN_URL for art with none stored, which is a
  // guess about where that art is served from. A 404 degrades to the no-preview
  // tile rather than a broken-image icon.
  const [brokenThumbs, setBrokenThumbs] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  // No kinds selected means all of them, rather than none — the filter is a
  // narrowing tool, so its resting state has to be "everything".
  const shown = assets.images.filter(
    (i) =>
      (!typeFilters.length || typeFilters.includes(i.type_code)) &&
      (!filter || i.s3_key.toLowerCase().includes(filter.toLowerCase())),
  )

  const onFile = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    setUploadError(null)
    try {
      const image = await assets.uploadImage(file, uploadType)
      onPick({ tc_image_id: image.tc_image_id, label: image.s3_key })
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose an image" size="large">
      <div className={styles.libFilters}>
        <input
          className={styles.input}
          value={filter}
          placeholder="Filter by name…"
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className={styles.chipToggles}>
          {IMAGE_TYPE_OPTIONS.map((o) => (
            <button
              key={o.value}
              className={`${styles.toggleChip} ${
                typeFilters.includes(o.value) ? styles.toggleChipOn : ''
              }`}
              onClick={() =>
                setTypeFilters((cur) =>
                  cur.includes(o.value)
                    ? cur.filter((v) => v !== o.value)
                    : [...cur, o.value],
                )
              }
            >
              {IMAGE_TYPE_LABELS[o.value] ?? o.value}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.libGrid}>
        <button
          className={`${styles.libItem} ${!value ? styles.libItemSel : ''}`}
          onClick={() => onPick({ tc_image_id: null })}
        >
          <span className={styles.libThumb}>
            <span className={styles.libNoThumb}>none</span>
          </span>
          <span className={styles.libName}>No image</span>
          <span className={styles.libMeta}>Leave this layer unset</span>
        </button>
        {shown.map((i) => (
          <button
            key={i.tc_image_id}
            className={`${styles.libItem} ${
              i.tc_image_id === value ? styles.libItemSel : ''
            }`}
            title={i.s3_key}
            onClick={() => onPick({ tc_image_id: i.tc_image_id, label: i.s3_key })}
          >
            {/* Checkerboard behind the thumbnail: plates and overlays are mostly
                transparent, and on a white card they look like nothing at all. */}
            <span className={styles.libThumb}>
              {/* The full-size original: the CMS has no image transform
                  service wired up, so a 1920x1080 PNG background is fetched
                  whole to draw an 84px tile. Cached after the first load. */}
              {i.cdn_url && !brokenThumbs.includes(i.tc_image_id) ? (
                <img
                  src={i.cdn_url}
                  alt=""
                  loading="lazy"
                  onError={() =>
                    setBrokenThumbs((cur) =>
                      cur.includes(i.tc_image_id) ? cur : [...cur, i.tc_image_id],
                    )
                  }
                />
              ) : (
                <span className={styles.libNoThumb}>no preview</span>
              )}
            </span>
            <span className={styles.libName}>{i.s3_key.split('/').pop()}</span>
            <span className={styles.libMeta}>
              {IMAGE_TYPE_LABELS[i.type_code] ?? i.type_code}
              {i.pixel_width && i.pixel_height ? ` · ${i.pixel_width}×${i.pixel_height}` : ''}
            </span>
          </button>
        ))}
        {!shown.length && (
          <p className={styles.emptyHint}>Nothing matches. Upload one below.</p>
        )}
      </div>

      <div className={styles.libUpload}>
        <div className={styles.secHead}>Upload a new image</div>
        <p className={styles.hint}>
          Pick what kind of asset this is — it’s recorded on the image itself, and
          it’s how the image is found later. It has no effect on the layer you’re
          editing.
        </p>
        <div className={styles.libFilters}>
          <Dropdown value={uploadType} options={IMAGE_TYPE_OPTIONS} onChange={setUploadType} />
          <button
            className={styles.uploadBtn}
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose file…'}
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
        {uploadError && <p className={styles.jsonError}>{uploadError}</p>}
      </div>
    </Modal>
  )
}

function ImageSourceField({
  layer,
  targeting,
  assets,
  onPatch,
  onReplace,
}: {
  layer: ImageLayer
  targeting: Targeting | undefined
  assets: AssetsBundle
  onPatch: (patch: Record<string, unknown>) => void
  onReplace: (next: Layer) => void
}) {
  // A canvas-filling layer is the background, so an upload from its picker is
  // almost certainly a background rather than a plate.
  const defaultTypeCode =
    layer.box?.fill === 'canvas' ? 'match_background' : 'matchcard_plate'
  const source = layer.source
  const choices = imageSourceChoices(targeting, source)
  const conditionField = conditionalFieldOf(source)

  const patchRules = (rules: ConditionalRule[]) =>
    onPatch({ source: { ...source, rules } })

  return (
    <div className={styles.field}>
      <div className={styles.secHead}>Image source</div>
      {/* One dropdown rather than kind + team + player + asset: the entity axes
          only produce a handful of combinations, and naming them removes the
          schema's 0-based "index" from the UI entirely. */}
      <Dropdown
        value={imageSourceKey(source)}
        options={choices.map((c) => ({ value: c.value, label: c.label }))}
        onChange={(v) => {
          const choice = choices.find((c) => c.value === v)
          if (choice) onPatch({ source: sourceFromChoice(choice, source) })
        }}
      />

      {source.kind === 'entity' && (
        <FallbackField layer={layer} onPatch={onPatch} onReplace={onReplace} />
      )}

      {source.kind === 'fixed' && (
        <>
          <label className={styles.fieldLabel}>Image</label>
          <ImagePicker
            value={source.tc_image_id}
            assets={assets}
            defaultTypeCode={defaultTypeCode}
            onChange={(ref) => onPatch({ source: { kind: 'fixed', ...ref } })}
          />
        </>
      )}

      {source.kind === 'conditional' && (
        <>
          <label className={styles.fieldLabel}>Depending on</label>
          {/* One option today: the schema's conditional `when` allows only
              category_in. The control exists so the shape is visible, and so
              adding a field is an entry in CONDITIONAL_FIELDS. */}
          <Dropdown
            value={conditionField.value}
            options={CONDITIONAL_FIELDS.map((f) => ({ value: f.value, label: f.label }))}
            onChange={() => {
              /* Single field for now; selecting it is a no-op. */
            }}
          />
          <p className={styles.hint}>
            Tried top to bottom — the first rule whose{' '}
            {conditionField.label.toLowerCase()} matches, and whose image loads,
            wins. If none match, the default is used.
          </p>
          <table className={styles.ruleTable}>
            <thead>
              <tr>
                {/* No number column: the rows are the order, the arrows change
                    it, and the note above says it's tried top to bottom. */}
                <th>When {conditionField.label.toLowerCase()} is</th>
                <th className={styles.ruleColImage}>Image</th>
                <th className={styles.ruleColActions} />
              </tr>
            </thead>
            <tbody>
              {(source.rules ?? []).map((rule, i) => {
                const rules = source.rules ?? []
                const move = (dir: -1 | 1) => {
                  const next = [...rules]
                  const [row] = next.splice(i, 1)
                  next.splice(i + dir, 0, row)
                  patchRules(next)
                }
                return (
                  <tr key={i}>
                    <td>
                      <ConditionValueChips
                        values={rule.when?.category_in ?? []}
                        field={conditionField}
                        onChange={(cats) => {
                          const next = [...rules]
                          next[i] = { ...rule, when: { category_in: cats } }
                          patchRules(next)
                        }}
                      />
                    </td>
                    <td className={styles.ruleColImage}>
                      <ImagePicker
                        value={rule.tc_image_id}
                        assets={assets}
                        defaultTypeCode={defaultTypeCode}
                        compact
                        onChange={(ref) => {
                          const next = [...rules]
                          next[i] = { when: rule.when, ...ref }
                          patchRules(next)
                        }}
                      />
                    </td>
                    <td className={styles.ruleColActions}>
                      <button
                        className={styles.ruleMoveBtn}
                        title="Try earlier"
                        disabled={i === 0}
                        onClick={() => move(-1)}
                      >
                        ↑
                      </button>
                      <button
                        className={styles.ruleMoveBtn}
                        title="Try later"
                        disabled={i === rules.length - 1}
                        onClick={() => move(1)}
                      >
                        ↓
                      </button>
                      <button
                        className={styles.ruleDelBtn}
                        title="Remove rule"
                        onClick={() => patchRules(rules.filter((_, j) => j !== i))}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                )
              })}
              {!(source.rules ?? []).length && (
                <tr>
                  <td colSpan={3} className={styles.ruleEmpty}>
                    No rules — every match uses the default below.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <button
            className={styles.addRuleBtn}
            onClick={() =>
              patchRules([
                ...(source.rules ?? []),
                { when: { category_in: [] }, tc_image_id: null },
              ])
            }
          >
            + Add category rule
          </button>
          <label className={styles.fieldLabel}>Default image (no rule matched)</label>
          <ImagePicker
            value={source.default?.tc_image_id ?? null}
            assets={assets}
            defaultTypeCode={defaultTypeCode}
            onChange={(ref) => onPatch({ source: { ...source, default: ref } })}
          />
        </>
      )}
    </div>
  )
}

/** The entity fallback: what to draw when a player's asset is missing. */
function FallbackField({
  layer,
  onPatch,
  onReplace,
}: {
  layer: ImageLayer
  onPatch: (patch: Record<string, unknown>) => void
  onReplace: (next: Layer) => void
}) {
  const fallback = layer.fallback
  const size: Point = fallback?.size ?? [400, 300]

  return (
    <>
      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={Boolean(fallback)}
          onChange={(e) => {
            if (e.target.checked) {
              onPatch({ fallback: { asset: 'flag', size, align: 'center' } })
            } else {
              onReplace(withoutKey(layer, 'fallback'))
            }
          }}
        />
        Fall back to another asset when missing
      </label>
      {fallback && (
        <div className={styles.row}>
          <div className={styles.inlineField}>
            <label className={styles.fieldLabel}>Asset</label>
            <Dropdown
              value={fallback.asset}
              options={ENTITY_ASSET_OPTIONS}
              onChange={(v) => onPatch({ fallback: { ...fallback, asset: v } })}
            />
          </div>
          <div className={styles.inlineField}>
            <label className={styles.fieldLabel}>W</label>
            <input
              className={styles.input}
              type="number"
              value={size[0]}
              onChange={(e) =>
                onPatch({ fallback: { ...fallback, size: [num(e.target.value), size[1]] } })
              }
            />
          </div>
          <div className={styles.inlineField}>
            <label className={styles.fieldLabel}>H</label>
            <input
              className={styles.input}
              type="number"
              value={size[1]}
              onChange={(e) =>
                onPatch({ fallback: { ...fallback, size: [size[0], num(e.target.value)] } })
              }
            />
          </div>
        </div>
      )}
    </>
  )
}

function ImageBoxField({
  layer,
  layers,
  path,
  canvas,
  onPatch,
}: {
  layer: ImageLayer
  layers: Layer[]
  path: LayerPath
  canvas: { width: number; height: number }
  onPatch: (patch: Record<string, unknown>) => void
}) {
  const box = layer.box ?? {}
  const size = box.size ?? [null, null]
  const mode = sizingModeOf(box)
  const modeInfo = SIZING_MODES.find((m) => m.value === mode)
  const fields = mode ? sizingFieldsFor(mode) : 'both'
  const otherIds = anchorTargets(layers, path).filter((id) => id !== 'canvas')

  const setSize = (next: [number | null, number | null]) =>
    onPatch({ box: { ...box, size: next } })

  return (
    <div className={styles.field}>
      <div className={styles.secHead}>Sizing</div>
      <Dropdown
        value={mode ?? ''}
        options={
          mode
            ? SIZING_MODES
            : [{ value: '', label: '— unrecognised, see below —' }, ...SIZING_MODES]
        }
        onChange={(v) =>
          onPatch({ box: boxForSizingMode(box, v as SizingMode, canvas) })
        }
      />
      {modeInfo ? (
        <p className={styles.hint}>{modeInfo.help}</p>
      ) : (
        // Three stored states the modes can't express. Named exactly, because
        // each renders differently and none is what an author would have meant.
        <p className={styles.gateInline}>
          {box.fit
            ? `This layer's fit is "${box.fit}", which the renderer has no branch for — it falls through to an exact resize.`
            : !box.size
              ? 'This layer has no size, so the renderer defaults it to 60px tall with the width from the image’s proportions.'
              : 'Both dimensions are blank, so the image renders at its own pixel size.'}{' '}
          Pick a mode above to make it explicit.
        </p>
      )}

      {(fields === 'both' || fields === 'width') && (
        <div className={styles.row}>
          <div className={styles.inlineField}>
            <label className={styles.fieldLabel}>Width</label>
            <input
              className={styles.input}
              type="number"
              value={size[0] ?? ''}
              onChange={(e) => setSize([num(e.target.value), size[1]])}
            />
          </div>
          {fields === 'both' && (
            <div className={styles.inlineField}>
              <label className={styles.fieldLabel}>Height</label>
              <input
                className={styles.input}
                type="number"
                value={size[1] ?? ''}
                onChange={(e) => setSize([size[0], num(e.target.value)])}
              />
            </div>
          )}
        </div>
      )}
      {fields === 'height' && (
        <div className={styles.row}>
          <div className={styles.inlineField}>
            <label className={styles.fieldLabel}>Height</label>
            <input
              className={styles.input}
              type="number"
              value={size[1] ?? ''}
              onChange={(e) => setSize([size[0], num(e.target.value)])}
            />
          </div>
        </div>
      )}

      {mode !== 'canvas' && (
        <>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={Boolean(layer.reserve_space)}
              onChange={(e) => onPatch({ reserve_space: e.target.checked || undefined })}
            />
            Reserve space when the image is missing
          </label>
          <p className={styles.hint}>
            Keeps an invisible box so layers anchored to this one don’t shift when
            the art is absent. Ignored for cover and full-canvas-width sizing,
            which have nothing to hold open.
          </p>
          <label className={styles.fieldLabel}>Suppress if this layer fell back</label>
          <Dropdown
            value={layer.skip_if_fell_back ?? ''}
            options={[
              { value: '', label: '— never suppress —' },
              ...otherIds.map((id) => ({ value: id, label: id })),
            ]}
            onChange={(v) => onPatch({ skip_if_fell_back: v || undefined })}
          />
          <p className={styles.hint}>
            For a flag paired with a headshot: if the headshot already fell back to
            the flag, drawing it twice looks wrong.
          </p>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------
function TextContentField({
  layer,
  onPatch,
}: {
  layer: TextLayer
  onPatch: (patch: Record<string, unknown>) => void
}) {
  const isHero = useContext(HeroTemplateCtx)
  const isStatic = 'static' in layer.content
  const boundStr = isStatic ? undefined : (layer.content as { bound: string }).bound
  const matchBound = parseMatchBound(boundStr)
  const bound = parseBound(matchBound ? undefined : boundStr)
  const source = isStatic ? 'static' : matchBound ? 'match' : 'bound'

  return (
    <div className={styles.field}>
      <div className={styles.secHead}>Content</div>
      <label className={styles.fieldLabel}>Source</label>
      <Dropdown
        value={source}
        options={[
          { value: 'bound', label: 'Player field' },
          // Hero-only: the matchcard schema rejects match.* bindings.
          ...(isHero || matchBound
            ? [{ value: 'match', label: 'Match start date/time' }]
            : []),
          { value: 'static', label: 'Static text' },
        ]}
        onChange={(v) =>
          onPatch({
            content:
              v === 'static'
                ? { static: '' }
                : v === 'match'
                  ? { bound: matchBoundPath(matchBound ?? 'start_time_display') }
                  : { bound: boundPath(bound.team, bound.index, bound.field) },
          })
        }
      />

      {source === 'match' && (
        <>
          <label className={styles.fieldLabel}>Field</label>
          <Dropdown
            value={matchBound ?? 'start_time_display'}
            options={MATCH_BOUND_OPTIONS}
            onChange={(v) =>
              onPatch({
                content: {
                  bound: matchBoundPath(v as (typeof MATCH_BOUND_OPTIONS)[number]['value']),
                },
              })
            }
          />
          <p className={styles.hint}>
            Pre-formatted in US Eastern by the render pipeline; the card
            regenerates when the match is rescheduled.
          </p>
        </>
      )}

      {isStatic ? (
        <>
          <label className={styles.fieldLabel}>Text</label>
          <input
            className={styles.input}
            value={(layer.content as { static: string }).static}
            placeholder="Text to render"
            onChange={(e) => onPatch({ content: { static: e.target.value } })}
          />
        </>
      ) : source === 'bound' ? (
        <>
          <label className={styles.fieldLabel}>Field</label>
          <Dropdown
            value={bound.field}
            options={BOUND_FIELD_OPTIONS}
            onChange={(v) =>
              onPatch({
                content: {
                  bound: boundPath(bound.team, bound.index, v as typeof bound.field),
                },
              })
            }
          />
          <div className={styles.row}>
            <div className={styles.inlineField}>
              <label className={styles.fieldLabel}>Team</label>
              <Dropdown
                value={bound.team}
                options={TEAM_OPTIONS}
                onChange={(v) =>
                  onPatch({
                    content: {
                      bound: boundPath(v as 'home' | 'away', bound.index, bound.field),
                    },
                  })
                }
              />
            </div>
            <div className={styles.inlineField}>
              <label className={styles.fieldLabel}>Index</label>
              <input
                className={styles.input}
                type="number"
                value={bound.index}
                onChange={(e) =>
                  onPatch({
                    content: {
                      bound: boundPath(bound.team, num(e.target.value), bound.field),
                    },
                  })
                }
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

/**
 * Upload a new font face. Family/weight/style are asked for rather than parsed
 * out of the filename: they are how the face is named everywhere afterwards, and
 * font filenames ("NeuePlak-ExtendedBlack.otf") don't split reliably into the
 * three. The API rejects a duplicate (family, weight, style) so the picker can't
 * end up with two identical-looking options.
 */
/**
 * Upload a new font face.
 *
 * Family, weight and style are read out of the font itself (its name table) and
 * prefilled rather than typed — but left editable, because splitting one style
 * string into weight + style is a heuristic and the triple is the face's
 * identity: it's what the generator resolves a face by, what the picker shows,
 * and what gets written into a template body. There's no endpoint to rename a
 * face afterwards, so it has to be right before upload, not after.
 */
function FontUpload({
  assets,
  onUploaded,
}: {
  assets: AssetsBundle
  onUploaded: (font: ArtworkAssetFont) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [family, setFamily] = useState('')
  const [weight, setWeight] = useState('')
  const [style, setStyle] = useState('')
  const [rawStyle, setRawStyle] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setFile(null)
    setFamily('')
    setWeight('')
    setStyle('')
    setRawStyle(null)
    setError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const onPickFile = async (picked: File | undefined) => {
    if (!picked) return
    setFile(picked)
    setReading(true)
    setError(null)
    try {
      const guess = await inspectArtworkTemplateFont(picked)
      setFamily(guess.font_family ?? '')
      setWeight(guess.font_weight ?? '')
      setStyle(guess.font_style ?? '')
      setRawStyle(guess.style_name)
    } catch (e) {
      // The file is still uploadable; the author just fills the fields in.
      setError(e instanceof Error ? e.message : 'Could not read that font')
    } finally {
      setReading(false)
    }
  }

  const submit = async () => {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const font = await assets.uploadFont(file, {
        font_family: family.trim(),
        font_weight: weight.trim() || undefined,
        font_style: style.trim() || undefined,
      })
      onUploaded(font)
      reset()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  if (!file) {
    return (
      <div className={styles.uploadRow}>
        <button className={styles.uploadBtn} onClick={() => fileRef.current?.click()}>
          Upload a font…
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".otf,.ttf"
          hidden
          onChange={(e) => void onPickFile(e.target.files?.[0])}
        />
        {error && <p className={styles.jsonError}>{error}</p>}
      </div>
    )
  }

  return (
    <div className={styles.uploadPanel}>
      <p className={styles.hint}>
        <strong>{file.name}</strong>
        {reading
          ? ' — reading…'
          : rawStyle
            ? ` — the font calls itself “${family} ${rawStyle}”.`
            : ''}
      </p>
      <div className={styles.row}>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Family</label>
          <input
            className={styles.input}
            value={family}
            placeholder="Neue Plak"
            onChange={(e) => setFamily(e.target.value)}
          />
        </div>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Weight</label>
          <input
            className={styles.input}
            value={weight}
            placeholder="Black"
            onChange={(e) => setWeight(e.target.value)}
          />
        </div>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Style</label>
          <input
            className={styles.input}
            value={style}
            placeholder="Extended"
            onChange={(e) => setStyle(e.target.value)}
          />
        </div>
      </div>
      <p className={styles.hint}>
        Will be listed as “{fontLabel({
          tc_font_id: '',
          font_family: family || 'Family',
          font_weight: weight || null,
          font_style: style || null,
          s3_key: '',
        })}”. Correct it if the split looks wrong — all three together identify
        the face, and it can’t be renamed later.
      </p>
      <div className={styles.uploadRow}>
        <button
          className={styles.uploadBtn}
          disabled={busy || reading || !family.trim()}
          onClick={() => void submit()}
        >
          {busy ? 'Uploading…' : 'Upload'}
        </button>
        <button className={styles.deleteBtn} disabled={busy} onClick={reset}>
          Cancel
        </button>
      </div>
      {error && <p className={styles.jsonError}>{error}</p>}
    </div>
  )
}

function TextStyleField({
  layer,
  assets,
  onPatch,
}: {
  layer: TextLayer
  assets: AssetsBundle
  onPatch: (patch: Record<string, unknown>) => void
}) {
  const { fonts } = assets
  const font = layer.font ?? { tc_font_id: null, size: 96 }
  const known = fonts.some((f) => f.tc_font_id === font.tc_font_id)

  return (
    <div className={styles.field}>
      <div className={styles.secHead}>Type</div>
      <label className={styles.fieldLabel}>Font</label>
      <Dropdown
        value={font.tc_font_id ?? ''}
        options={[
          { value: '', label: '— default —' },
          ...(font.tc_font_id && !known
            ? [
                {
                  value: font.tc_font_id,
                  label:
                    font.label ?? `Unlisted font (${font.tc_font_id.slice(0, 8)}…)`,
                },
              ]
            : []),
          ...fonts.map((f) => ({ value: f.tc_font_id, label: fontLabel(f) })),
        ]}
        onChange={(id) => {
          const f = fonts.find((x) => x.tc_font_id === id)
          onPatch({
            font: f
              ? { tc_font_id: id, label: fontLabel(f), size: font.size }
              : { tc_font_id: null, size: font.size },
          })
        }}
      />
      <FontUpload
        assets={assets}
        onUploaded={(f) =>
          onPatch({ font: { tc_font_id: f.tc_font_id, label: fontLabel(f), size: font.size } })
        }
      />
      <div className={styles.row}>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Size</label>
          <input
            className={styles.input}
            type="number"
            value={font.size ?? 96}
            onChange={(e) => onPatch({ font: { ...font, size: num(e.target.value) } })}
          />
        </div>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Max width</label>
          <input
            className={styles.input}
            type="number"
            value={layer.max_width ?? ''}
            placeholder="none"
            onChange={(e) =>
              onPatch({ max_width: e.target.value === '' ? undefined : num(e.target.value) })
            }
          />
        </div>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Min size</label>
          <input
            className={styles.input}
            type="number"
            value={layer.min_size ?? ''}
            placeholder="none"
            onChange={(e) =>
              onPatch({ min_size: e.target.value === '' ? undefined : num(e.target.value) })
            }
          />
        </div>
        <div className={styles.colorField}>
          <label className={styles.fieldLabel}>Color</label>
          <input
            className={styles.colorInput}
            type="color"
            value={layer.color ?? '#FFFFFF'}
            onChange={(e) => onPatch({ color: e.target.value.toUpperCase() })}
          />
        </div>
      </div>
      <p className={styles.hint}>
        Long text shrinks toward <strong>min size</strong> to fit <strong>max width</strong>.
      </p>
      <label className={styles.fieldLabel}>Case</label>
      <Dropdown
        value={layer.transform ?? ''}
        options={TEXT_TRANSFORM_OPTIONS}
        onChange={(v) => onPatch({ transform: v || undefined })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------
function ShapeStyleField({
  layer,
  onPatch,
}: {
  layer: ShapeLayer
  onPatch: (patch: Record<string, unknown>) => void
}) {
  const size = layer.size ?? [0, 0]
  return (
    <div className={styles.field}>
      <div className={styles.secHead}>Shape</div>
      <div className={styles.row}>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Width</label>
          <input
            className={styles.input}
            type="number"
            value={size[0]}
            onChange={(e) => onPatch({ size: [num(e.target.value), size[1]] })}
          />
        </div>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Height</label>
          <input
            className={styles.input}
            type="number"
            value={size[1]}
            onChange={(e) => onPatch({ size: [size[0], num(e.target.value)] })}
          />
        </div>
        <div className={styles.colorField}>
          <label className={styles.fieldLabel}>Color</label>
          <input
            className={styles.colorInput}
            type="color"
            value={layer.color ?? '#000000'}
            onChange={(e) => onPatch({ color: e.target.value.toUpperCase() })}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Opacity (0–255)</label>
          <input
            className={styles.input}
            type="number"
            min={0}
            max={255}
            value={layer.opacity ?? 255}
            onChange={(e) =>
              onPatch({ opacity: Math.min(255, Math.max(0, num(e.target.value))) })
            }
          />
        </div>
        <div className={styles.inlineField}>
          <label className={styles.fieldLabel}>Skew X</label>
          <input
            className={styles.input}
            type="number"
            value={layer.skew_x ?? 0}
            onChange={(e) => onPatch({ skew_x: num(e.target.value) || undefined })}
          />
        </div>
      </div>
      <p className={styles.hint}>
        Skew slants the left and right edges — the angled plate behind the names.
      </p>
    </div>
  )
}

// ===========================================================================
// Anchor 3×3 grid
// ===========================================================================
function AnchorGrid({
  value,
  onChange,
}: {
  value?: AnchorPoint
  onChange: (v: AnchorPoint) => void
}) {
  return (
    <div className={styles.anchorGrid}>
      {ANCHOR_POINTS.map((p) => (
        <button
          key={p}
          className={`${styles.anchorCell} ${value === p ? styles.anchorCellSel : ''}`}
          title={p}
          onClick={() => onChange(p)}
        />
      ))}
    </div>
  )
}

// ===========================================================================
// Template settings (shown when no layer is selected)
// ===========================================================================
function TemplateSettings({
  draft,
  templateKey,
  name,
  onRename,
  onPatchTemplate,
  onPatchTargeting,
  onDropUnknownKeys,
}: {
  draft: TemplateBody | null
  templateKey: string
  /** tc_artwork_template.name — the identity row, shown as the page heading. */
  name: string
  onRename: (value: string) => void
  onPatchTemplate: (patch: Partial<TemplateBody>) => void
  onPatchTargeting: (patch: Partial<TemplateBody['targeting']>) => void
  onDropUnknownKeys: () => void
}) {
  // Hero templates are picked by key from a hero-banner slide, never selected
  // by targeting/aspect — those controls and warnings would only mislead.
  const isHero = useContext(HeroTemplateCtx)
  if (!draft) return null
  const strays = unknownTopLevelKeys(draft)
  const canvas = draft.canvas
  const targeting = draft.targeting
  const categories = targeting.categories ?? []
  const types = targeting.types ?? []
  const sizeProblem = canvasProblem(canvas)

  const toggleIn = <T extends string>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  return (
    <>
      {strays.length > 0 && (
        <div className={styles.strayKeys}>
          <strong>Publish will reject {strays.length} unrecognized key(s)</strong>
          <p>
            {strays.map((k, i) => (
              <span key={k}>
                {i > 0 && ', '}
                <code>{k}</code>
              </span>
            ))}{' '}
            — left over from the older template format. The engine ignores them;
            the schema doesn’t.
          </p>
          <button onClick={onDropUnknownKeys}>Remove them</button>
        </div>
      )}

      <div className={styles.formGrid}>
        <div className={styles.field}>
          <div className={styles.secHead}>Template</div>
          <label className={styles.fieldLabel}>Name</label>
          <input className={styles.input} value={name} onChange={(e) => onRename(e.target.value)} />
          {/* Read-only: the key is the stable identifier the engine selects on
              and there is no rename endpoint. Shown here so it's still
              discoverable without putting it in the title bar. */}
          <label className={styles.fieldLabel}>Key</label>
          <input className={styles.input} value={templateKey} readOnly disabled />
        </div>

        <div className={styles.field}>
          <div className={styles.secHead}>Canvas</div>
          <div className={styles.row}>
            <div className={styles.inlineField}>
              <label className={styles.fieldLabel}>Width</label>
              <input
                className={`${styles.input} ${sizeProblem ? styles.inputBad : ''}`}
                type="number"
                min={1}
                max={MAX_CANVAS_DIMENSION}
                value={canvas.width}
                onChange={(e) =>
                  onPatchTemplate({ canvas: { ...canvas, width: num(e.target.value) } })
                }
              />
            </div>
            <div className={styles.inlineField}>
              <label className={styles.fieldLabel}>Height</label>
              <input
                className={`${styles.input} ${sizeProblem ? styles.inputBad : ''}`}
                type="number"
                min={1}
                max={MAX_CANVAS_DIMENSION}
                value={canvas.height}
                onChange={(e) =>
                  onPatchTemplate({ canvas: { ...canvas, height: num(e.target.value) } })
                }
              />
            </div>
          </div>
          {sizeProblem && (
            <p className={styles.gateInline}>
              {sizeProblem} The preview is paused until this is in range.
            </p>
          )}
          {isHero ? (
            <p className={styles.hint}>
              Hero slides pick this template by key — desktop slots render
              1920×475, mobile slots 4:3.
            </p>
          ) : (
            <p className={styles.hint}>
              The canvas aspect is also what selects this template for a render —
              there is no separate aspect setting.
            </p>
          )}
          {!isHero && !canvasIsSelectable(canvas) && (
            <p className={styles.gateInline}>
              This canvas is {canvas.width}×{canvas.height}, which is neither 16:9
              nor 4:3 — the only ratios the engine is ever asked for. The template
              renders here, but no real card will select it until the engine learns
              this ratio and something requests it.
            </p>
          )}
        </div>

        {!isHero && (
        <div className={styles.field}>
          <div className={styles.secHead}>Targeting</div>
          <p className={styles.hint}>
            Which matches this template covers. The engine matches a card to a
            template on these plus the canvas aspect.
          </p>
          <label className={styles.fieldLabel}>Categories</label>
          <div className={styles.chipToggles}>
            {TARGETING_CATEGORIES.map((c) => (
              <button
                key={c}
                className={`${styles.toggleChip} ${categories.includes(c) ? styles.toggleChipOn : ''}`}
                onClick={() => onPatchTargeting({ categories: toggleIn(categories, c) })}
              >
                {c}
              </button>
            ))}
          </div>
          <label className={styles.fieldLabel}>Types</label>
          <div className={styles.chipToggles}>
            {MATCH_TYPES.map((t) => (
              <button
                key={t}
                className={`${styles.toggleChip} ${types.includes(t) ? styles.toggleChipOn : ''}`}
                title={
                  matchTypeIsRedundant(t)
                    ? 'A mixed-doubles match is matched as doubles, so this on its own selects nothing.'
                    : undefined
                }
                onClick={() => onPatchTargeting({ types: toggleIn(types, t) as MatchType[] })}
              >
                {t.replace('_', ' ')}
                {matchTypeIsRedundant(t) && <span className={styles.chipNote}>†</span>}
              </button>
            ))}
          </div>
          {!targetingIsSelectable(targeting) && (
            <p className={styles.gateInline}>
              Pick <strong>singles</strong> or <strong>doubles</strong> — those are
              the only types selection asks for. A mixed-doubles match is matched
              as doubles, so <code>mixed doubles</code> alone selects nothing.
            </p>
          )}
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={Boolean(targeting.default_for_unknown_category)}
              onChange={(e) =>
                onPatchTargeting({ default_for_unknown_category: e.target.checked })
              }
            />
            Default for unknown categories
          </label>
          <div className={styles.row}>
            <div className={styles.inlineField}>
              <label className={styles.fieldLabel}>Resolution</label>
              <input
                className={styles.input}
                value={targeting.resolution ?? ''}
                placeholder="1080"
                onChange={(e) => onPatchTargeting({ resolution: e.target.value })}
              />
            </div>
          </div>
        </div>
        )}
      </div>
    </>
  )
}

// ===========================================================================
// Raw-JSON fallback for vod_overlay / plate / draw template types
// ===========================================================================
function RawJsonEditor({
  templateType,
  value,
  error,
  onChange,
}: {
  templateType: string
  value: string
  error: string | null
  onChange: (v: string) => void
}) {
  return (
    <div className={styles.formArea}>
      <div className={styles.card}>
        <p className={styles.hint}>
          The visual editor covers <strong>matchcard</strong> templates. This is a{' '}
          <strong>{templateType}</strong> template — edit its JSON directly below.
        </p>
        <textarea
          className={`${styles.jsonArea} ${error ? styles.jsonAreaError : ''}`}
          value={value}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
        />
        {error && <p className={styles.jsonError}>{error}</p>}
      </div>
    </div>
  )
}
